<template>
  <div class="address-autofill-input-div">
    <div v-if="!hideQuestion" :class="[{ 'question-require': required }, 'mt-3']">{{ question }}</div>
    <el-select
      ref="address-search-selector"
      v-model="inputContent"
      :disabled="disabled"
      :class="{ disabled: disabled }"
      filterable
      allow-create
      remote
      clearable
      class="address-search-selector"
      popper-class="address-search-selector-dropdown"
      :remote-method="(search_text) => remoteMethod(search_text)"
      :loading="loading"
      :placeholder="placeholder"
      :suffix-icon="null"
      :show-arrow="false"
      @change="(command) => handleSelectedItem(command)"
    >
      <el-option
        v-for="item in options"
        :key="item.place_id"
        :label="item.description"
        :value="item"
      />
    </el-select>
  </div>
  <div id="dummy-map" style="display: none;"></div>
</template>

<script>
import { ElNotification } from 'element-plus';

export default {
  name: 'AddressAutofilledUnit',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    question: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    default: {
      type: String,
      default: ''
    },
    hideQuestion: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    displayMode: {
      type: String,
      default: ''
    },
    preferCountryCode: {
      type: String,
      default: ''
    }
  },
  emits: ['autofillAddress', 'handleValueChange', 'update:modelValue'],
  data() {
    return {
      inputContent: this.default || this.modelValue || '',
      loading: false,
      options: [],
      originalValue: this.default || this.modelValue || ''
    };
  },
  watch: {
    inputContent(newVal) {
      // Only emit string values, not objects (when an option is selected)
      if (typeof newVal === 'string') {
        this.$emit('handleValueChange', newVal, this.question);
      } else if (newVal && typeof newVal === 'object' && newVal.structured_formatting) {
        // When object is selected from dropdown, emit the street address
        const streetAddress = newVal.structured_formatting.main_text;
        this.$emit('handleValueChange', streetAddress, this.question);
      }
    },
    modelValue(newVal) {
      if (this.inputContent !== newVal) {
        this.inputContent = newVal;
        this.originalValue = newVal;
      }
    }
  },
  methods: {
    remoteMethod(search_text) {
      if (search_text != null && search_text.trim().length !== 0) {
        this.loading = true;
        setTimeout(() => {
          const params = {
            input: search_text
          };

          if (this.preferCountryCode) {
            const c = String(this.preferCountryCode).toLowerCase();
            params.componentRestrictions = { country: [c] };
          }

          const autocompleteService = new window.google.maps.places.AutocompleteService();
          autocompleteService.getPlacePredictions(params, (res) => {
            // Include user's input as a custom option
            this.options = res || [];
            this.loading = false;
          });
        }, 300);
      } else {
        this.options = [];
        this.loading = false;
      }
    },

    handleSelectedItem(command) {
      // If user entered a custom value (string) - not a place object
      if (typeof command === 'string') {
        this.inputContent = command;
        this.originalValue = command;

        // Parse the custom address manually
        const parts = command.split(',').map(p => p.trim());
        const [streetAddress, city, state, zipcode, country] = [...parts, '', '', '', '', ''].slice(0, 5);

        this.$emit('autofillAddress', {
          street_number: '',
          route: streetAddress,
          subpremise: '',
          locality: city,
          administrative_area_level_1: state,
          postal_code: zipcode,
          country: country || 'US',
          streetAddress: streetAddress,
          city,
          state,
          zipcode,
          country: country || 'US'
        });
        return;
      }

      // User selected from dropdown - place object
      this.inputContent = command.structured_formatting.main_text;
      this.originalValue = command.structured_formatting.main_text;

      const request = {
        placeId: command.place_id
      };

      const map = document.getElementById('dummy-map');
      const placeService = new window.google.maps.places.PlacesService(map);

      const address_dict = [
        'premise',
        'subpremise',
        'street_number',
        'route',
        'neighborhood',
        'locality',
        'administrative_area_level_2',
        'administrative_area_level_1',
        'country',
        'postal_code',
        'postal_code_suffix',
        'postal_code_prefix'
      ];

      const filterAddress = (address_components, dict) => {
        const output = {};
        for (const component of address_components) {
          for (const type of component.types) {
            if (dict.includes(type)) {
              output[type] = component.short_name;
              break;
            }
          }
        }
        if (output.postal_code_suffix != null) {
          output.postal_code = `${output.postal_code}-${output.postal_code_suffix}`;
        }
        if (output.postal_code_prefix != null) {
          output.postal_code = output.postal_code_prefix + '-' + output.postal_code;
        }
        if (output.country == "GB") {
          output.locality = output.locality || output.administrative_area_level_2;
        }
        return output;
      };

      placeService.getDetails(request, (res) => {
        if (!res || !res.address_components) {
          console.warn('No address_components found in Google placeService response:', res);
          return;
        }

        const filteredSelectedAddress = filterAddress(res.address_components, address_dict);

        this.$emit('autofillAddress', filteredSelectedAddress);
      });
    }
  }
};
</script>

<style scoped>
.address-autofill-input-div {
  width: 100%;
  font-family: 'Roboto', 'Lato', sans-serif;
  font-size: 14px;
  line-height: 16px;
  font-weight: 400;
  color: #292929;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.question-require::after {
  content: '*';
  color: red;
}

.mt-3 {
  margin-top: 12px;
}

/* Select styling */
:deep(.address-search-selector) {
  width: 100%;
  height: 100%;
}

:deep(.address-search-selector .el-select__wrapper) {
  height: 38px;
  border: none !important;
  box-shadow: none !important;
}

:deep(.address-search-selector .el-input__wrapper) {
  height: 40px !important;
  border-radius: 4px;
  border: 1px solid #C4C4C4 !important;
  box-shadow: none !important;
  padding: 0 12px;
}

:deep(.address-search-selector .el-input__wrapper:hover) {
  border-color: #A8A8A8 !important;
}

:deep(.address-search-selector .el-input__wrapper.is-focus) {
  border-color: #409EFF !important;
}

:deep(.address-search-selector .el-input__inner) {
  height: 40px !important;
  line-height: 40px !important;
  /* Match the va-responsive-components-library TextInput/SingleSelector
     text styling (.text-input-field) so this field doesn't stand out next
     to its siblings in the same address row. */
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0.15px;
  color: #17181c;
}

:deep(.address-search-selector .el-input__inner::placeholder) {
  color: #999;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0.15px;
}

:deep(.address-search-selector .el-input__suffix) {
  display: none !important;
}

/* Disabled state */
.disabled :deep(.el-input__wrapper) {
  border: none !important;
  background: #f4f4f4 !important;
  box-shadow: none !important;
  cursor: not-allowed !important;
}

.disabled :deep(.el-input__inner) {
  cursor: not-allowed !important;
}

/* Dropdown options styling */
:deep(.address-search-selector-dropdown .el-select-dropdown__item) {
  padding: 8px 12px;
  font-size: 14px;
  line-height: 20px;
}

:deep(.address-search-selector-dropdown .el-select-dropdown__item:hover) {
  background-color: #f5f7fa;
}

:deep(.address-search-selector-dropdown .el-select-dropdown__item.selected) {
  color: #409EFF;
  font-weight: 500;
}
</style>
